<template>
  <div v-if="state.product" class="product-page-wrapper">
    <div class="product-main-content desk-hor-mob-col">
      <section v-if="imagesSwap?.length" class="section-wrapper section-wrapper--pdp-images">
        <ProductImages :images="imagesSwap" :product-name="productName" class="component-wrapper" />
      </section>

      <section class="product-data-container section-wrapper section-wrapper--pdp-buy-box">
        <div class="product-data-content flex-col component-wrapper">
          <h1 class="product-title">
            {{ productNameSwap }}
          </h1>
          <span v-if="backorderMessage" class="backorder-message">({{ backorderMessage }})</span>

          <YotpoStarRating v-if="baseProductId" :product-id="baseProductId" :yotpo-key="yotpoKey" />

          <PriceSection
            :sku="sku"
            :quantity="state.quantity"
            :show-payment-options="true"
            :subscription-price="subscriptionPrice"
          />

          <LazyUrgencyMessaging
            v-if="selectedVariant?.messaging?.urgency_messaging?.message"
            :urgency-messaging="selectedVariant.messaging.urgency_messaging"
          />

          <ProductOptions
            :sku="baseProduct.sku"
            :option-default-override-id="state.optionDefaultOverrideId"
            :option-default-override-sku="state.optionDefaultOverrideSku"
            :show-comparison-chart-link="showComparisonChartLink"
            :product-variations="productVariations"
            :is-pdp="true"
            @product-options:update="updatedVariant"
            @modifiers-changed="setProductModifiers($event, true)"
          />

          <Snackbar v-if="snackbarProps && !snackbarDyCampaign?.campaign_id" v-bind="snackbarProps" />
          <DYCampaign
            v-if="snackbarDyCampaign"
            :campaign_id="snackbarDyCampaign.campaign_id"
            :campaign_title="snackbarDyCampaign.campaign_title"
            :variations="snackbarDyCampaignVariations"
          />
          <div v-if="productLinks && productLinks?.length" class="fire-pits-section mgn-btm-1-pt5">
            <!-- <div class="size bold">
              Size: <span class="sizename">{{ findMatchingFirePit(productUrl).sizeName }}</span>
            </div> -->
            <div class="pits-container">
              <NuxtLink
                v-for="product in productLinks"
                :key="product.title"
                :to="localePath(product.url)"
                :class="['fire-pit-option', { selected: '/p/' + productUrl === product.url }]"
              >
                <div v-if="product.sell_point" class="sellpoint ff-text-500" v-text="product.sell_point" />
                <div class="title ff-text-500">{{ product?.display_title || product?.title }}</div>
                <PriceSection
                  :sku="'/p/' + productUrl === product.url ? sku : product?.sku"
                  :is-pdp="false"
                  :show-info-icon="false"
                />
                <div v-if="product.dimensions" class="size-info" v-text="product.dimensions" />
              </NuxtLink>
            </div>
          </div>

          <client-only>
            <Etch
              v-if="showEtchingSection && state.product?.etching_section"
              ref="etchModal"
              :etching-section="state.product?.etching_section"
              :selected-variant="selectedVariant"
              :product-name="productNameSwap"
              :images="etchingImagesSwap"
              :has-variants="hasVariants"
              :bc-variant="state.bcVariant"
              :bc-variants="state.options"
              :product-modifiers="state.productModifiers"
              :modal-open="state.etchModalOpen"
              @disabled="setEtchA2CDisabled"
              @custom-object="setEtchingProductModifiers"
              @close-etch-modal="resetEtchProductModifiers"
              @type-selected="resetEtchProductModifiers"
            >
              <template #productOptions>
                <ProductOptions
                  :sku="baseProduct.sku"
                  :product-variations="productVariations"
                  :existing-choice="state.bcVariant"
                  @product-options:update="updatedVariant"
                  @modifiers-changed="setProductModifiers($event, true)"
                />
              </template>
              <template #addToCart>
                <Stepper
                  :min="state.quantityStart"
                  :counter-start="state.quantity"
                  @stepper-update="updateQuantity($event)"
                />
                <AddToCartButton
                  :product="cartProduct"
                  :images="state?.etchImage?.length ? state.etchImage : imagesSwap"
                  :variant="state.bcVariant"
                  :is-variant="hasVariants"
                  :quantity="state.quantity"
                  :disable-btn="state.etchA2CDisabled || bcVariantDisabled"
                  @added-to-cart="
                    () => {
                      etchAddToCart();
                    }
                  "
                />
              </template>
            </Etch>
          </client-only>

          <div class="button-container flex-inline between reverse-container-items">
            <div id="a2c-button" ref="addToCartButton" class="a2c-button-wrapper pdp-add-2-cart">
              <div class="sticky-a2c-content">
                <div class="flex-col">
                  <div class="product-name hidden">
                    {{ productNameSwap }}
                  </div>
                  <div v-if="state?.productModifiers?.length > 0">
                    <a
                      class="options-link hidden ul"
                      href="#options-section"
                      aria-label="Go to Options"
                      @click="scrollToElementById('options-section', $event)"
                    >
                      <span v-for="(modifier, index) in state.productModifiers" :key="index + modifier.option_id">
                        {{ (index > 0 ? ' ' : '') + modifier.value }}
                      </span>
                    </a>
                  </div>
                  <a
                    v-else-if="
                      state?.options?.length > 0 || (productDetails?.subscription_item && state.subscriptionOptions)
                    "
                    class="options-link hidden ul"
                    :href="state.options && state.options?.length > 0 ? '#options-section' : '#subscription-options'"
                    aria-label="Go to Options"
                    @click="
                      scrollToElementById(
                        state?.options?.length > 0 ? 'options-section' : 'subscription-options',
                        $event
                      )
                    "
                  >
                    {{ state?.bcVariant?.label ? state.bcVariant.label : '' }}
                    {{ productDetails?.subscription_item && state.subscriptionOptions ? subscriptionDisplay() : '' }}
                  </a>
                </div>

                <AddToCartButton
                  :product="cartProduct"
                  :images="imagesSwap"
                  :variant="state.bcVariant"
                  :is-variant="hasVariants"
                  :quantity="state.quantity"
                  :disable-btn="
                    buttonDisabledQuantity || maxInCart || bcVariantDisabled || selectedVariant?.variant_disabled
                  "
                  :add-to-cart-msg-additional="
                    state.addToCartScrolled && isMobileOrTablet
                      ? `- ${currencySymbol + ((subscriptionPrice || currentPrice) * state.quantity).toFixed(2)}`
                      : undefined
                  "
                />
              </div>
            </div>

            <Stepper
              :min="state.quantityStart"
              :counter-start="state.quantity"
              :max="state.maxQuantity"
              @stepper-update="updateQuantity($event)"
            />
            <SubscriptionOptions
              v-if="state?.subscriptionOptions?.length > 0"
              :subscription-options="state.subscriptionOptions"
              :current-price="currentPrice"
              :sku="sku"
              @selected-sub="subscriptionChange"
              @freq-change="frequencyChange"
            />
            <LazyCartError
              v-if="buttonDisabledQuantity || maxInCart"
              :error-custom="buttonDisabledQuantity || maxInCart ? 'Max Quantity Reached' : undefined"
            />
          </div>

          <div
            id="amzn-buy-now"
            data-site-id="05ngq7gqvj"
            data-widget-id="w-q0vHC2Yd4n2pcvy6vT7kMa"
            :data-sku="state?.bcVariant?.sku || baseProduct?.sku"
            :data-quant="state.quantity"
          />

          <!-- <div
            v-if="localeStore?.isUsLocale && !backorderMessage && estimatedArrivalDate"
            class="shipping-arrival flex-inline bold"
          >
            <CalendarEventLine />
            {{ estimatedArrivalDate }}
          </div> -->

          <!-- eslint-disable-next-line vue/no-v-html -->
          <h2 v-if="productDescriptionSwap" class="product-description" v-html="productDescriptionSwap" />

          <PdpBundlingSection
            v-if="asyncData?.productAccessories?.length"
            :product="state.product"
            :accessories="asyncData.productAccessories"
            :name="productNameSwap"
          />

          <div :class="['product-offer']">
            <!-- <div class="bold txt-center width-100">Worry-Free Guarantee</div> -->
            <div class="flex-inline between width-100">
              <div v-if="showLifetimeWarranty" class="product-offer-block flex-col cntr">
                <div class="product-offer-svg">
                  <ShieldCheckLine />
                </div>
                <div class="product-offer-title">{{ useTranslate('PDP.lifetimeWarranty') }}</div>
              </div>
              <div v-if="showFreeShipping" class="product-offer-block flex-col cntr free-shipping">
                <div class="product-offer-svg">
                  <TruckLine />
                </div>
                <div class="product-offer-title">
                  {{ useTranslate('PDP.freeShipping') + currencySymbol + cartStore.freeShipThreshold }}
                </div>
              </div>
              <div v-if="showFreeReturns" class="product-offer-block flex-col cntr">
                <div class="product-offer-svg returns" />
                <div class="product-offer-title">{{ useTranslate('PDP.freeReturns') }}</div>
              </div>
            </div>
          </div>

          <Accordion
            v-if="newWhatsIncludedDefined"
            :default-open="true"
            :title="useTranslate('PDP.whatsIncluded') || 'What’s Included'"
            desc="req prop - override with slot"
          >
            <template #content>
              <div class="dynamic-content">
                <component
                  :is="getAccordionComponent(item)"
                  v-for="(item, i) in whatsIncludedItems"
                  :key="`included_item_${i}`"
                  :to="item?.product?.url ? localePath(item.product.url) : undefined"
                  class="dropdown-product"
                >
                  <CloudinaryImage
                    :public-id="item.publicId"
                    :options="{
                      width: 64,
                      height: 64,
                      // gravity: 'center',
                      quality: 'auto:eco',
                    }"
                    :alt="item.title"
                  />
                  <p>
                    <strong
                      >{{ item.title
                      }}<em v-if="item?.product?.backorder_message"> ({{ item.product.backorder_message }})</em></strong
                    >
                  </p>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div v-html="item.description" />
                </component>
              </div>
            </template>
          </Accordion>

          <Accordion
            v-for="(item, index) in accordionSwap"
            :key="`accordion_item_${index}`"
            :default-open="newWhatsIncludedDefined ? false : index === 0"
            :title="item.accordion_title"
            :desc="item.content"
          />
        </div>
      </section>
    </div>

    <div class="product-dynamic-content flex-col">
      <!-- index === 1 should be above video -->
      <PageSection
        v-for="(section, index) in state.contentStackData?.sections"
        :key="`${index}-section`"
        render-mode="pdp"
        :style="`order: ${index};`"
        :section="section"
        :class="getSectionName(section)"
      />
      <section
        v-if="!hideReviews"
        class="section-wrapper section-wrapper--pdp-reviews"
        :style="`order: ${state.contentStackData?.sections?.length || 0};`"
      >
        <div
          class="yotpo yotpo-main-widget"
          :style="'margin-left: auto; margin-right: auto; max-width: 86%; width: 100%;'"
          :data-product-id="baseProductId"
          :data-price="currentPrice"
          :data-currency="currencyCode"
          :data-name="productName"
          :data-url="strippedUrl"
          :data-image-url="images && images.length ? imageUrls[0] : ''"
        />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import { BcVariant, ProductV2 } from '@solo-stove/types/contentstack/stove/product';
import { CloudinaryAsset } from '@solo-stove/types/contentstack/stove/cloudinary';
import { QueryTypes } from '~/server/utils/contentstack';
import { useCloudinary } from '~/composables/cloudinary';
import { contentstackRequest } from '~/util/contentstack/api';
import { getProductRichResults, metaGen } from '~/util/metadata';
import { getFirstIndex } from '~/util/arrays';
import { extractComponent, getAssetPublicId } from '~/util/contentstack/csHelpers';
import { getLocalStorageItem, setLocalStorageItem, recentlyViewedKey } from '~/util/localStorage';
import { scrolledPast, scrollToElementById } from '~/util/eventHandler';
import { maxCartProducts } from '~/util/bigcommerce';
import { useProductDataStore } from '~/store/product-data';
import { mapCtsCartItemsToGtmCartItems, baseEcommerceEventStructure, GtmEventType } from '~/util/analytics';
import { refreshKlarna } from '~/util/klarna';
import { useLocaleStore } from '~/store/locale';
import { useCartStore } from '~/store/cart';
import { useDeviceStore } from '~/store/device';
import { useRootStore } from '~/store';

type productIdsQueryRes = {
  system: {
    uid: string;
  };
  product_bundling_options: {
    products_in_bundleConnection: {
      edges: {
        node: {
          system: {
            uid: string;
          };
        };
      }[];
    };
  };
};

const { regionHeaderName, regionLocaleProperties } = useRegionRequestSettings();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const route = useRoute();
const { $bus }: any = useNuxtApp();
const productUrl = route?.params?.product as string;
const cartStore = useCartStore();
const rootStore = useRootStore();
const localeStore = useLocaleStore();
const deviceStore = useDeviceStore();
const productDataStore = useProductDataStore();
const { currencyCode } = useLocaleStore();
const { locale } = useI18n();
const event = useRequestEvent();
const cloudinary = useCloudinary();
const yotpoKey = rootStore.yotpoAppKey;

// setup refs
const etchModal = ref<any | null>(null);
const addToCartButton = ref<HTMLElement | null>(null);

const regionCode = regionLocaleProperties?.value?.regionCode;
const { data: asyncData, error: asyncError } = await useAsyncData(async () => {
  const productSlug = route.params.product as string;
  const [productRes, productSectionsRes, productIdsRes] = await Promise.all([
    contentstackRequest(QueryTypes.product, `/p/${productSlug}`, {
      event,
      query: { locale: locale?.value, regionCode },
    }),
    contentstackRequest(QueryTypes.productSections, `/p/${productSlug}`, {
      event,
      query: { locale: locale?.value },
    }),
    contentstackRequest(QueryTypes.productIds, `/p/${productSlug}`, {
      event,
      query: { locale: locale?.value, regionCode },
    }),
  ]);
  // console.log('Product DATA', productRes);
  // console.log('Product SECTIONS CTS DATA', productSectionsRes);
  if (!productRes) throw createError({ statusCode: 404, message: 'Page not found' });

  const productIndex: ProductV2 = getFirstIndex(productRes?.data?.all_product_v2?.items);
  const productIds: productIdsQueryRes = getFirstIndex(productIdsRes?.data?.all_product_v2?.items) || {};
  const sections = getFirstIndex(productSectionsRes?.data?.all_product_v2?.items);
  const { system } = productIds;

  if (!productIndex) throw createError({ statusCode: 404, message: 'Page not found' });

  const product: ProductV2 = {
    ...productIndex,
    system,
    sections,
  };
  const contentStackData = {
    ...product,
    ...sections,
  };

  let subscriptionOptions = [] as any[];
  if (contentStackData?.product_details?.subscription_item) {
    try {
      const res: any = await useFetchApiLayer(
        `/recharge-product-plan/${contentStackData?.product_offerings?.bc_primary_product?.product?.id}`,
        {
          headers: {
            [regionHeaderName]: regionCode,
          },
        }
      );
      subscriptionOptions = res?.plans;
      if (!subscriptionOptions?.length) throw new Error('Subscription Options not of correct type.');
    } catch (_err: any) {
      console.error(`Error in retrieving subscription options. Page affected: /p/${productSlug}`, _err); // eslint-disable-line no-console
      subscriptionOptions = [];
    }
  }

  const options = mapVariants(product.product_offerings?.product_variations);

  const productAccessories = product?.product_bundling_options?.products_in_bundleConnection?.edges?.length
    ? product.product_bundling_options.products_in_bundleConnection.edges.map((item: any) => item?.node)
    : null;

  return {
    contentStackData,
    product,
    productAccessories,
    options,
    quantity: product?.product_details?.min_quantity,
    quantityStart: product?.product_details?.min_quantity,
    maxQuantity: product?.product_details?.max_quantity,
    subscriptionOptions,
  };
});

useAsyncErrorHandling({
  error: asyncError.value,
});

const asyncProduct = asyncData.value?.product;
const state = reactive({
  // Begin AsyncData Merge Portion
  contentStackData: asyncData.value?.contentStackData || ({} as any),
  product: asyncProduct as ProductV2,
  options: asyncData.value?.options || ([] as any[]),
  quantity: asyncData.value?.quantity || 1,
  quantityStart: asyncData.value?.quantityStart || 1,
  maxQuantity: asyncData.value?.maxQuantity,
  subscriptionOptions: asyncData.value?.subscriptionOptions || ([] as any[]),
  bcVariant: {} as any,
  // End AsyncData Merge Portion
  optionIndex: 0,
  etchModalOpen: route?.query?.customizeOpen === 'true',
  optionDefaultOverrideId: (route?.query?.option_id && +route.query.option_id) || (undefined as any),
  optionDefaultOverrideSku: (route?.query?.sku && route.query.sku) || (undefined as any),
  productModifiers: [] as any[],
  etchingProductModifiers: [] as any[],
  selectedSubscription: {} as any,
  subscriptionType: 'onetime',
  etchA2CDisabled: true,
  etchImage: [] as any,
  addToCartScrolled: false,
});

// initiate before meta section
const productOfferings = computed(() => state.product?.product_offerings);
const productVariations = computed(() => productOfferings?.value?.product_variations);
const selectedVariant = computed(() => {
  const variations = productVariations?.value;
  const allVariants = state.optionIndex !== -1 && !!variations ? variations : null;
  return allVariants?.[state.optionIndex];
});
const selectedVariantBcVariant = computed(() => selectedVariant.value?.bc_variant);
const baseProduct = computed(() => productOfferings?.value?.bc_primary_product?.product);
const hasVariants = computed<boolean>(() => state.options?.length > 1);
const sku = computed(() => (hasVariants.value ? selectedVariantBcVariant.value?.sku || '' : baseProduct.value?.sku));
const parentSku = computed(() =>
  hasVariants.value && selectedVariantBcVariant.value?.sku !== baseProduct.value?.sku ? baseProduct.value?.sku : null
);
const priceInfo = computed(() => productDataStore.getPriceBySku(sku.value));
// const currentPrice = computed(() => subscriptionPrice.value || priceInfo.value?.calculated_price);
const currentPrice = computed(() => priceInfo.value?.calculated_price);
// const originalPrice = computed(() => priceInfo.value?.price);
const images = computed(() =>
  productOfferings?.value?.base_image_set?.length ? productOfferings.value.base_image_set || [] : []
);
const imagesSwap = computed(() => {
  const newImages: Array<any> = selectedVariant.value?.image?.length ? selectedVariant.value?.image : [];
  return newImages.concat(images.value);
});
const primaryImage = computed(() => {
  const imgFallback = images.value?.[0]?.public_id || undefined;
  const imgPrimary = selectedVariant.value?.image?.[0]?.public_id || imgFallback;
  return imgPrimary ? cloudinary.generateImageUrl(imgPrimary, { width: 550, height: 550 }) : '';
});
// initiate before meta section

const seo = asyncProduct?.seo;
const metaGenCustom = metaGen(rootStore?.brandSettings, {
  ...(seo || {}),
  ...(primaryImage.value !== '' && { ogImage: primaryImage.value }),
}) as any;
const productRichResults = getProductRichResults(
  asyncProduct,
  asyncProduct?.product_offerings?.bc_primary_product?.product,
  asyncProduct?.product_offerings?.base_image_set,
  rootStore,
  currencyCode,
  config?.public?.baseUrl + route?.path,
  currentPrice?.value
);

const script = [productRichResults];
useHead({
  ...metaGenCustom,
  script,
});

// Computed - Custom defs
const currencySymbol = computed(() => localeStore.currencySymbol);
const productDetails = computed(() => state.product?.product_details);
const isMobileOrTablet = computed(() => deviceStore.isMobileOrTablet);
const strippedUrl = computed(() => config?.public?.baseUrl + route?.path);
const productName = computed<string>(() => productOfferings?.value?.product_title || '');
const productNameSwap = computed<string>(() => {
  return selectedVariant.value ? selectedVariant.value?.display_title || productName.value : productName.value;
});
// TODO - update types
const productLinks = computed(() => {
  const products = (productDetails as any)?.value?.product_link;
  const mappedProducts = products?.map((product: any) => {
    const extractedProduct = extractComponent(product?.productConnection);
    const variants = extractedProduct?.product_offerings?.product_variations;
    return {
      ...extractedProduct,
      display_title: product?.display_title,
      sell_point: product?.sell_point,
      dimensions: product?.dimensions,
      sku: variants?.length
        ? variants?.[0]?.bc_variant?.sku
        : extractedProduct?.product_offerings?.bc_primary_product?.product?.sku,
    };
  });
  return mappedProducts;
});
const etching = computed(() => {
  return state.product?.etching_section?.customization?.useModifiers ? state.product?.etching_section : undefined;
});
const showEtchingSection = computed<boolean>(() => {
  const enableEtching = state.product?.etching_section?.enable_etching || false;
  return !!etching.value && (typeof enableEtching === 'boolean' ? enableEtching : true);
});
// TODO - update
// const showComparisonChartLink = computed<boolean>(() => state.product?.show_comparison_chart_link);
const showComparisonChartLink = computed<boolean>(() => false);
const backorderMessage = computed<string>(() => selectedVariant.value?.messaging?.backorder_message || '');
const etchingImagesSwap = computed<any[]>(() => {
  const newImages: Array<any> = selectedVariant.value
    ? (selectedVariant.value?.etching_image?.length && selectedVariant.value?.etching_image) || []
    : [];
  return newImages.concat(imagesSwap.value);
});
const imageUrls = computed<string[]>(() => {
  if (images?.value?.length)
    return images.value.map((image: CloudinaryAsset) =>
      cloudinary.generateImageUrl(image?.public_id, { width: 550, height: 550 })
    );
  return [];
});
const productDescriptionSwap = computed(() => selectedVariant.value?.description || '');
const baseProductId = computed<number>(() => baseProduct?.value?.id);
const snackbarProps = computed(() => extractComponent(productDetails?.value?.snackbar));
const snackbarDyCampaign = computed<any | undefined>(() => productDetails?.value?.snackbar_dy_campaign);
const snackbarDyCampaignVariations = computed<any[]>(() => {
  if (!snackbarDyCampaign.value) return [];
  const arr =
    snackbarDyCampaign.value?.variations?.map((variation: any) => {
      return { ...variation, variation_type: 'content_referenceConnection' };
    }) || [];
  return arr;
});
const subscriptionPrice = computed<number | undefined>(() => {
  const price = currentPrice.value;
  const subDiscountAmt = state.selectedSubscription?.discount_amount;
  const subDiscountType = state.selectedSubscription?.discount_type;
  if (subDiscountAmt && subDiscountType) {
    const percentage =
      subDiscountType === 'percentage' && typeof +subDiscountAmt === 'number' ? +subDiscountAmt / 100 : 0;
    const val = price - price * percentage;
    return Number(val.toFixed(2));
  }
  return undefined;
});
const cartProduct = computed(() => {
  const variantId = selectedVariantBcVariant.value?.id;
  return {
    ...baseProduct?.value,
    ...(state.productModifiers?.length > 0 && { variantId: selectedVariantBcVariant.value?.id }),
    ...(state.etchingProductModifiers?.length > 0 && {
      option_selections: [...state.etchingProductModifiers],
    }),
    ...(state.subscriptionType === 'subscription' && { subData: state.selectedSubscription }),
    variantId,
  };
});
const hideReviews = computed<boolean>(() => !!productDetails?.value?.hide_reviews);
const showLifetimeWarranty = computed<boolean>(() =>
  productDetails?.value?.lifetime_warranty !== null ? productDetails.value.lifetime_warranty : true
);
const showFreeReturns = computed<boolean>(() =>
  productDetails?.value?.free_returns !== null ? productDetails.value.free_returns : true
);
const showFreeShipping = computed<boolean>(() =>
  productDetails?.value?.free_shipping !== null ? productDetails.value.free_shipping : true
);
const accordionItems = computed<any[]>(() => {
  const items =
    selectedVariant.value?.accordion_itemsConnection?.edges?.map((item: any) => item?.node?.accordion_items) || [];
  return pushSubItems(items);
});
const whatsIncludedItems = computed<any[]>(() => {
  return (
    selectedVariant.value?.whats_included_items?.map((item: any) => {
      return {
        title: item?.title,
        description: item?.description,
        product: extractComponent(item?.productConnection),
        publicId: getAssetPublicId(item.image),
      };
    }) || []
  );
});
const newWhatsIncludedDefined = computed<boolean>(() => !!whatsIncludedItems.value?.length);
const accordionSwap = computed<any[]>(() => {
  const backup: any[] = [...accordionItems.value];
  const newItems: any = selectedVariant.value
    ? selectedVariant.value?.accordion_itemsConnection?.edges?.map((item: any) => item?.node?.accordion_items)
    : [];
  const oldWhatsIncluded = backup.findIndex((item) => {
    if (item.accordion_title?.toLowerCase()?.indexOf('included') > -1) return true; // old what's included section
    return false;
  });
  // end TODO
  const newAllItems = pushSubItems(newItems);
  if (newWhatsIncludedDefined.value && oldWhatsIncluded > -1) {
    // if new version of whats inlcluded is defined, remove old whats included section
    // expected that whats included is first
    return newAllItems.length > 1 ? newAllItems?.slice(1) : backup?.slice(1);
  } else {
    return newAllItems.length > 1 ? newAllItems : backup;
  }
  // return newAllItems;
});
const buttonDisabledQuantity = computed<boolean>(() => {
  return state.maxQuantity
    ? state.quantity > state.maxQuantity || state.quantity < state.quantityStart
    : state.quantity < state.quantityStart;
});
const bcVariantDisabled = computed<boolean>(() => {
  return selectedVariantBcVariant.value?.purchasing_disabled || false;
});

const maxInCart = computed<boolean>(() => {
  return maxCartProducts(cartStore.products, baseProduct.value, state.quantity, state.maxQuantity);
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const estimatedArrivalDate = computed<string>(() => {
  const today = new Date();
  const daysAhead: number = state?.product?.product_details?.estimated_shipping_days || 5;
  const futureDate = new Date(today.getTime() + daysAhead * 24 * 60 * 60 * 1000);
  const month = futureDate.toLocaleString('default', { month: 'long' });
  const day = futureDate.getDate();
  const dayOfWeek = futureDate.toLocaleString('default', { weekday: 'long' });
  let suffix;
  if (day === 1 || day === 21 || day === 31) {
    suffix = 'st';
  } else if (day === 2 || day === 22) {
    suffix = 'nd';
  } else if (day === 3 || day === 23) {
    suffix = 'rd';
  } else {
    suffix = 'th';
  }
  return `Order now and receive it by ${dayOfWeek}, ${month} ${day}${suffix}`;
});

onMounted(async () => {
  window.addEventListener('scroll', checkA2cScrolledPast(10), true);
  window.addEventListener('resize', checkA2cScrolledPast(250), true);

  refreshKlarna();
  updateRecentlyViewed();
  resetEtchProductModifiers();
  await nextTick();
  initBwp();

  const pageViewProduct = {
    ...state.product,
    hasVariants: hasVariants.value,
    sku: sku.value,
    parentSku: parentSku.value,
    backorderMessage: backorderMessage.value,
  };
  const gaPageViewProduct = [
    {
      product: {
        id: baseProduct.value?.id,
        sku: sku.value,
        parentSku: parentSku.value || undefined,
        name: productNameSwap.value,
        brand: rootStore.brandSettings?.brandName,
        image: primaryImage.value,
        price: subscriptionPrice.value || priceInfo.value?.calculated_price,
        originalPrice: priceInfo.value?.price,
        category: state.product?.product_taxonomy?.category?.join(', '),
      },
      ...(selectedVariantBcVariant.value && { variant: selectedVariantBcVariant.value }),
      hasVariants: hasVariants.value,
      quantity: 1,
    },
  ];

  const gtm = useGtm();
  const gtmItems = mapCtsCartItemsToGtmCartItems(gaPageViewProduct);
  const gtmEvent = baseEcommerceEventStructure({ eventType: GtmEventType.viewItem, items: gtmItems });
  gtm?.trackEvent(gtmEvent);

  const metaPixel = useMetaPixel();
  const contentName = gtmItems?.[0]?.item_name;
  metaPixel?.trackEvent({ event: 'ViewContent', eventItems: gtmItems, contentName });

  $bus.emit('pageView', {
    url: document.URL,
    data: {
      product: pageViewProduct,
      pageType: 'Product Page',
      referrerUrl: document?.referrer || '',
      productUrl: window?.location?.pathname || `/p/${productUrl}`,
    },
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', checkA2cScrolledPast(10), true);
  window.removeEventListener('resize', checkA2cScrolledPast(250), true);
});

// Methods - Functions instead of arrows for hoisting
// function addBwpScript() {
//   if (isDefined('bwp-script')) {
//     initBwp();
//     return Promise.resolve(true);
//   }
//   const loadScript = loadjs('https://code.buywithprime.amazon.com/bwp.v1.js', 'bwp-script', {
//     before: (_path: string, el: HTMLElement) => {
//       el.setAttribute('id', 'bwp-script');
//     },
//     returnPromise: true,
//   });
//   return loadScript
//     .then(() => {
//       initBwp();
//       return Promise.resolve(true);
//     })
//     .catch((error: unknown) => {
//       console.warn(error); // eslint-disable-line no-console
//       return Promise.resolve(false);
//     });
// }
function getAccordionComponent(item: any) {
  if (item?.product?.url) {
    return resolveComponent('NuxtLink');
  }
  return 'div';
}
// function findMatchingFirePit(url: string): any {
//   return productLinks?.value?.find((item: any) => item.url === url) || '';
// }
function etchAddToCart(): void {
  etchModal.value?.clearText();
  etchModal.value?.toggleEtchModal();
}
function setEtchA2CDisabled(isBadWord: boolean): void {
  state.etchA2CDisabled = isBadWord;
}
function checkA2cScrolledPast(debounceDuration: number) {
  return debounce(() => {
    scrolledPast('a2c-button', 'a2c-pinned');
    state.addToCartScrolled = addToCartButton.value?.classList?.contains('scrolled-past') || false;
  }, debounceDuration);
}
function subscriptionDisplay(): string {
  const subPrefs = state.selectedSubscription?.subscription_preferences;
  if (subPrefs) {
    const frequency = subPrefs?.charge_interval_frequency;
    const interval = subPrefs?.interval_unit;
    return state.selectedSubscription?.type === 'subscription'
      ? `Delivered Every ${frequency > 1 ? frequency + ' ' : ''}${interval + (frequency > 1 ? 's' : '')}`
      : '';
  }
  return 'One-Time Purchase';
}
async function nextTickRefreshKlarna() {
  await nextTick(); // makes sure 'data-purchase-amount' on klarna html has updated in DOM
  refreshKlarna();
}

function refreshBwp(variant: BcVariant) {
  const bwp = (window as any)?.bwp;
  const sku = variant?.sku;
  if (bwp && sku) {
    try {
      const test = bwp.updateWidget(sku);
      console.log('BWP UPDATE', test);
    } catch (err) {
      console.log('FAILED BWP UPDATE');
      console.error(err);
    }
  }
}

function initBwp() {
  const bwp = (window as any)?.bwp;
  if (bwp) {
    try {
      // eslint-disable-next-line no-new
      new bwp.constructor();
    } catch (err) {
      console.log('FAILED BWP INIT');
      console.error(err);
    }
  }
}

async function updateQuantity(quantity: number) {
  state.quantity = quantity;
  await nextTickRefreshKlarna();
}
async function updatedVariant(updatedVariant: any) {
  state.bcVariant = updatedVariant;
  let locatedIndex = 0;
  state.options?.find((option: any, i: number) => {
    if (option.id === updatedVariant?.id) {
      locatedIndex = i;
      return true;
    }
    return false;
  });
  state.optionIndex = locatedIndex;
  refreshBwp(updatedVariant);
  await nextTickRefreshKlarna();
}
function resetEtchProductModifiers() {
  state.etchingProductModifiers = [];
}

function setProductModifiers(
  modData: { type: string; data: { [index: string]: any } | string },
  setFullData = false
): void {
  if (setFullData) {
    state.productModifiers = modData as any;
    return; // do not continue
  }
  if (state.bcVariant) {
    // options and option-sets for this setup
    state.productModifiers.push({
      option_id: state.bcVariant?.option_id,
      option_value: state.bcVariant?.id,
      name: state.bcVariant?.option_display_name,
      value: state.bcVariant?.label,
    });
  }
}

function setEtchingProductModifiers(etchCustomObject: {
  type: 'text' | 'college' | 'nfl';
  data: {
    modifier: { name: string; option_id: number; option_value: number | string; value: string; valueId: 0 };
    surcharge: { name: string; option_id: number; option_value: number; value: number; valueId: 0 };
  };
}): void {
  state.etchingProductModifiers = [etchCustomObject.data.modifier, etchCustomObject.data.surcharge];
}

const wrappedSetLocalStorageItem: typeof setLocalStorageItem = (key, value) => {
  try {
    setLocalStorageItem(key, value);
  } catch (err) {
    // TODO: Handle these failures more gracefully.
    console.warn('failure to set local storage item', key, value);
    console.error(err);
  }
};

function updateRecentlyViewed() {
  const currentRecViewed = getLocalStorageItem(recentlyViewedKey) as ProductV2[] | null;
  const currentProduct = {
    ...state.product,
    url: window?.location?.pathname || `/p/${productUrl}`,
  };
  if (currentRecViewed?.length) {
    // MAX - 4 items
    const alreadyExists = currentRecViewed.find(
      (product) => product?.product_offerings?.bc_primary_product?.product?.id === baseProductId?.value
    );
    if (!alreadyExists) {
      if (currentRecViewed?.length > 3) {
        // remove oldest item if there are already 4
        currentRecViewed.pop();
      }
      // Place most recent to front of array
      currentRecViewed.unshift(currentProduct);
      wrappedSetLocalStorageItem(recentlyViewedKey, JSON.stringify(currentRecViewed));
    }
  } else {
    wrappedSetLocalStorageItem(recentlyViewedKey, JSON.stringify([currentProduct]));
  }
}
function subscriptionChange(e: any): void {
  state.selectedSubscription = e;
}
function frequencyChange(e: string): void {
  state.subscriptionType = e;
}
function getSectionName(section: any): string {
  let sectionName = section?.section_name;
  sectionName = sectionName ? sectionName.toLowerCase().replace(/\s/g, '-') : '';
  return sectionName;
}

// Methods - used to be at top of script before refactor
function mapVariants(variations: ProductV2['product_offerings']['product_variations']) {
  return (
    variations?.map((productVariation: any) => {
      return {
        ...productVariation?.bc_variant,
        optionSelectionDesc: productVariation?.description,
      };
    }) || []
  );
}

function pushSubItems(items: any[]): any[] {
  const allItems: any = [];
  if (items?.length) {
    items?.forEach((item: any) => {
      item?.forEach((subItem: any) => allItems.push(subItem));
    });
  }
  return allItems;
}
</script>

<style lang="scss">
.modal-open {
  .yotpo-main-widget,
  .yotpo-display-wrapper,
  .y-image-wrapper {
    z-index: -1;
  }
}
</style>

<style lang="scss" scoped>
:deep(.product-main-content) {
  justify-content: space-between;
  .product-images-wrapper,
  .product-data-container {
    @include local-mixins.desktop {
      position: sticky;
      z-index: 2;
      top: 174px;
      height: fit-content;
    }
  }
  @include local-mixins.desktop {
    max-width: calc(local-vars.$xxl-content-max-width - 2rem);
    margin: 0 auto;
  }
  @include local-mixins.xxl {
    max-width: calc(local-vars.$xl-content-max-width - 2rem);
  }
  .snackbar-wrapper {
    margin-bottom: 1.5rem;
  }
}
.component-wrapper {
  padding-left: #{local-functions.rem-calc(15)};
  padding-right: #{local-functions.rem-calc(15)};
}
// Start Product Data Section
.product-data-container {
  width: 100%;
  :deep(.pp-container) {
    margin-top: 5px;
    width: 100%;
  }
}
.backorder-message {
  font-weight: 500;

  color: $color-primary-600;
  font-size: 1.5rem;
  margin-bottom: 5px;
  @include local-mixins.tablet_and_mobile {
    font-size: 1.125rem;
  }
}

.product-title {
  color: $color-neutral-cool-900;
  line-height: 1.15;
  margin-bottom: 2px;
  @include local-mixins.tablet_and_mobile {
    font-size: 2rem;
  }
}

@include local-mixins.desktop {
  .product-data-content {
    width: calc(100% - 2rem);
    margin-left: auto;
  }
}
// End Product Data Section
.product-dynamic-content {
  margin-top: #{local-functions.rem-calc(50px)};
  @include local-mixins.desktop {
    margin-top: #{local-functions.rem-calc(80px)};
  }
}
.product-offer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 8px;
  margin-bottom: 1.5rem;
  gap: 12px;
  width: 100%;
  border-radius: 4px;
  @include local-mixins.desktop {
    padding: 16px 32px;
  }
  .product-offer-block {
    display: flex;
    margin-bottom: 0;
    width: -webkit-fill-available;
  }
  .product-offer-svg {
    &.returns {
      @include local-mixins.backgroundMaskImage(
        local-functions.svg-img-source('shipping-box.svg'),
        $color-neutral-black,
        24px,
        24px
      );
    }
  }
  .product-offer-title {
    font-weight: normal;
    font-size: 0.75rem;
    text-align: center;
    color: $color-neutral-cool-900;
    line-height: 130%;
    margin-top: 0.5rem;
  }
}
.product-description {
  // TODO - p style
  font-size: 14px;
  font-weight: normal;
  line-height: 150%;
  color: $color-neutral-cool-900;
  margin-bottom: 1.5rem;
  p {
    font-size: 14px;
  }
}
.fire-pits-section {
  .sellpoint {
    font-size: 0.875rem;
    color: $color-primary-600;
    margin-bottom: 4px;
  }
  .size {
    margin-bottom: 8px;
    .sizename {
      font-weight: normal;
    }
  }
  .title {
    color: $color-neutral-cool-900;
    font-size: 1rem;
    line-height: 1.5;
  }
  .size-info {
    font-size: 0.75rem;
    color: $color-neutral-cool-600;
    margin-top: 4px;
  }
  .pits-container {
    display: grid;
    grid-auto-rows: 1fr;
    gap: #{local-functions.rem-calc(10)};
    @include local-mixins.desktop {
      gap: 8px;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
    }
  }

  .fire-pit-option {
    display: grid;
    grid-auto-columns: 1fr 1fr;
    grid-auto-rows: 1fr;
    align-items: center;
    padding: #{local-functions.rem-calc(5)} #{local-functions.rem-calc(10)};
    border: 1px solid $color-neutral-cool-300;
    border-radius: 4px;
    width: 100%;
    @include local-mixins.desktop {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: auto;
      flex: 1;
      padding: 16px 6px;
    }
    .sellpoint {
      justify-self: stretch;
      align-self: stretch;
      grid-row: 1;
      grid-column: 1;
      display: flex;
      align-items: center;
      // border-right: 1px solid $color-neutral-cool-100;
      // background-color: $color-neutral-warm-50;
      margin: 0 0 #{local-functions.rem-calc(5)};
      // padding: #{local-functions.rem-calc(5)} #{local-functions.rem-calc(10)};
      @include local-mixins.desktop {
        background-color: transparent;
        border: none;
        padding: 0;
        margin: 0 0 #{local-functions.rem-calc(8)};
      }
    }
    .title {
      grid-row: 2;
      grid-column: 1;
      line-height: 1;
      margin: 0;
      // padding: 0 #{local-functions.rem-calc(10)};
      @include local-mixins.desktop {
        align-self: flex-start;
        padding: 0;
        margin: 0 0 #{local-functions.rem-calc(2)};
      }
    }
    .price-container {
      order: 3;
      grid-row: 2;
      grid-column: 3;
      justify-self: flex-end;
      align-self: center;
      // padding-right: #{local-functions.rem-calc(10)};
      margin: 0;
      @include local-mixins.desktop {
        align-self: flex-start;
        padding: 0;
        margin: 0 0 #{local-functions.rem-calc(8)};
      }
    }
    .size-info {
      order: 4;
      margin: 0;
      grid-row: 3;
      grid-column: 1;
      // justify-self: flex-end;
      // align-self: center;
      // padding-right: #{local-functions.rem-calc(10)};
      @include local-mixins.desktop {
        align-self: flex-start;
        padding: 0;
        margin: 0;
      }
    }
    img {
      margin-right: 8px;
    }
    :deep(.price-container) {
      .current-price {
        font-size: 0.875rem;
      }
      .orig-price {
        margin-left: 8px;
        display: none;
      }
      .orig-price .strikethrough {
        font-size: 0.875rem;
      }
      .orig-price .msrp-price {
        font-size: 0.75rem !important;
      }
      .savings-cta {
        display: none;
      }
    }
    &:hover:not(.selected) {
      border: 1px solid $color-neutral-black;
    }
    &.selected {
      background-color: #f7f7f7;
      border-color: $color-primary-500;
      box-shadow: 0 0 0 1px $color-primary-500;
      @include local-mixins.desktop {
        margin: 0;
      }
    }
  }
}
.button-container {
  $animation-length: 0.25s;

  flex-wrap: wrap;
  margin-bottom: #{local-functions.rem-calc(28)};
  .pdp-add-2-cart.scrolled-past {
    @include local-mixins.tablet_and_mobile {
      border-top: 1px solid $color-neutral-cool-100;
      background-color: $color-neutral-white;
      padding: 0.75rem 1rem 1rem 1rem;
      position: fixed;
      bottom: 0;
      z-index: map.get(local-vars.$zindex, 'dropdown');
      left: 0;
      width: 100vw;
      animation: slide-up $animation-length cubic-bezier(0.4, 0, 0.2, 1) backwards;
      + .stepper-comp-wrapper {
        margin-right: auto;
      }
      .product-name {
        font-weight: 500;
        font-size: 1rem;
        @include local-mixins.desktop {
          font-size: 1.25rem;
        }
      }
      .options-link {
        color: $color-primary-600;
        font-size: 0.875rem;
        text-transform: capitalize;
        @include local-mixins.desktop {
          font-size: 1rem;
        }
      }
      .add-to-cart-wrapper {
        margin-top: 1rem;
        margin-left: 0;
      }
      .hidden {
        display: block;
      }
      .btn {
        width: calc(100vw - 32px);
      }
    }
    // Keeping this in case we want to revert
    // @include local-mixins.desktop {
    //   top: 148px;
    //   bottom: unset;
    //   border-top: none;
    //   border-bottom: 1px solid $color-neutral-cool-100;
    //   animation: fade-in 0.2s ease-out;
    //   .sticky-a2c-content {
    //     display: flex;
    //     justify-content: space-between;
    //     align-items: center;
    //     margin: 0 auto;
    //     max-width: 90.75rem;
    //   }
    //   .add-to-cart-wrapper {
    //     margin-top: 0;
    //     max-width: fit-content;
    //   }
    // }
  }
  .a2c-button-wrapper,
  .add-to-cart-wrapper {
    flex-grow: 2;
    width: auto;
  }
}
.urgency-message {
  margin-top: 0;
  margin-bottom: 1rem;
}
.reverse-container-items {
  flex-direction: row-reverse;
  justify-content: space-between;
  .stepper-comp-wrapper {
    margin-right: 1rem;
  }
}
:deep(.accordion) {
  .dropdown-product {
    padding: 0.5rem 0;
    .cloudinary-image-container {
      grid-area: image;
    }
    p:nth-child(3) {
      margin-bottom: #{local-functions.rem-calc(6)};
    }
    + .dropdown-product {
      border-top: 1px solid $color-neutral-cool-200;
    }
  }
}
.shipping-arrival {
  margin-bottom: 24px;
  font-size: 0.875rem;
  :deep(svg) {
    margin-right: 5px;
    @include local-mixins.desktop {
      height: 28px;
      width: 28px;
    }
  }
}
:deep(.video-wrapper) {
  @include local-mixins.xxl {
    padding: 0 2rem;
  }
  @include local-mixins.tablet_and_mobile {
    padding: 0 1rem;
  }
}
:deep(.section-wrapper--pdp-images .video-wrapper) {
  padding: 0;
}
</style>
